<template>
  <div class="todo-list-container">
    <a-form layout="inline" class="search-form">
      <a-form-item>
        <a-select v-model="searchForm.category_id" placeholder="选择分类" :loading="categoryLoading" style="width: 180px" allowClear>
          <a-select-option v-for="category in categoryList" :key="category.id" :value="category.id">
            {{ category.category_path }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item>
        <a-input v-model="searchForm.participant" placeholder="输入处理人" @pressEnter="handleSearch" allow-clear />
      </a-form-item>
      <a-form-item>
        <a-input v-model="searchForm.subject" placeholder="输入待办主题" @pressEnter="handleSearch" allow-clear />
      </a-form-item>
      <a-form-item>
        <a-select v-model="searchForm.status" placeholder="待办状态" style="width: 180px" allowClear>
          <a-select-option v-for="status in statusOptions" :key="status.value" :value="status.value">
            {{ status.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item>
        <a-select v-model="searchForm.is_overdue" placeholder="超时状态" style="width: 120px" allowClear>
          <a-select-option :value="1">已超时</a-select-option>
          <a-select-option :value="0">未超时</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item>
        <a-button type="primary" @click="handleSearch">搜索</a-button>
        <a-button @click="handleReset">重置</a-button>
      </a-form-item>
    </a-form>

    <div class="table-wrapper">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :pagination="false"
        :loading="loading"
        rowKey="id"
        size="middle"
        bordered
        :scroll="{ y: tableScrollY }"
      >
        <template slot="status" slot-scope="status">
          <a-tag :color="statusColorMap[status]">
            {{ statusMap[status] || status }}
          </a-tag>
        </template>
        <template slot="create_time" slot-scope="record">
          <div class="compact-time">
            <div>{{ record.create_time }}</div>
            <div style="margin-top: -3px">{{ record.update_time }}</div>
          </div>
        </template>
        <template slot="cancel_time" slot-scope="record">
          <div class="compact-time">
            <div>{{ record.cancel_time }}</div>
            <div style="margin-top: -3px">{{ record.complete_time }}</div>
          </div>
        </template>
        <template slot="operation" slot-scope="record">
          <a-button size="small" @click="viewDetail(record)">查看详情</a-button>
        </template>
      </a-table>
    </div>

    <div class="pagination-wrapper">
      <a-pagination
        v-model="pagination.current"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :page-size-options="['20', '50', '100', '200']"
        show-size-changer
        show-quick-jumper
        :show-total="total => `共 ${total} 条`"
        @change="handlePageChange"
        @showSizeChange="handlePageSizeChange"
      />
    </div>

    <a-modal
      title="待办详情"
      :visible="detailDialogVisible"
      width="70%"
      :footer="null"
      :mask-closable="false"
      @cancel="detailDialogVisible = false"
      :after-close="onDetailClosed"
    >
      <a-spin :spinning="detailLoading">
        <div class="detail-content">
          <template v-if="currentRow.id && !detailLoading">
            <div class="detail-section">
              <div class="section-title">基本信息</div>
              <a-descriptions :column="2" bordered>
                <a-descriptions-item label="ID">{{ currentRow.id }}</a-descriptions-item>
                <a-descriptions-item label="编号">{{ currentRow.code }}</a-descriptions-item>
                <a-descriptions-item label="主题">{{ currentRow.subject }}</a-descriptions-item>
                <a-descriptions-item label="分类">{{ currentRow.category_path || currentRow.category_name }}</a-descriptions-item>
                <a-descriptions-item label="状态">
                  <a-tag v-if="currentRow.status === 0" color="red">未处理</a-tag>
                  <a-tag v-else-if="currentRow.status === 1">已取消</a-tag>
                  <a-tag v-else-if="currentRow.status === 2" color="green">已完成</a-tag>
                  <a-tag v-else-if="currentRow.status === 3" color="orange">待确认</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="是否逾期">
                  <a-tag v-if="currentRow.is_overdue" color="red">已逾期</a-tag>
                  <a-tag v-else color="green">未逾期</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="创建时间">{{ currentRow.create_time }}</a-descriptions-item>
                <a-descriptions-item label="更新时间">{{ currentRow.update_time }}</a-descriptions-item>
                <a-descriptions-item v-if="currentRow.complete_time" label="完成时间">{{ currentRow.complete_time }}</a-descriptions-item>
                <a-descriptions-item v-if="currentRow.cancel_time" label="取消时间">{{ currentRow.cancel_time }}</a-descriptions-item>
                <a-descriptions-item label="逾期时间">{{ currentRow.expected_time }}</a-descriptions-item>
                <a-descriptions-item label="逾期说明">{{ currentRow.human_timeout || '-' }}</a-descriptions-item>
              </a-descriptions>
            </div>

            <div v-if="currentRow.participant && currentRow.participant.length" class="detail-section">
              <div class="section-title">参与人</div>
              <a-descriptions :column="2" bordered>
                <a-descriptions-item v-for="p in currentRow.participant" :key="p.id" :label="'参与人'">
                  {{ p.username }}
                </a-descriptions-item>
              </a-descriptions>
            </div>

            <div v-if="currentRow.content_list && currentRow.content_list.length" class="detail-section">
              <div class="section-title">内容列表</div>
              <div v-for="(content, idx) in currentRow.content_list" :key="content.id" class="content-card">
                <div class="content-card-header">
                  <span class="content-index">内容 #{{ idx + 1 }}</span>
                  <a-tag v-if="content.handle_status === 0" color="red">未处理</a-tag>
                  <a-tag v-else-if="content.handle_status === 2" color="green">已完成</a-tag>
                </div>
                <a-descriptions :column="2" bordered size="small">
                  <a-descriptions-item label="唯一标识">{{ content.uni_code }}</a-descriptions-item>
                  <a-descriptions-item label="SKU">
                    <div class="sku-list">
                      <a-tag v-for="sku in parseSku(content.sku)" :key="sku" class="sku-tag">{{ sku }}</a-tag>
                    </div>
                  </a-descriptions-item>
                  <a-descriptions-item label="平台ID">{{ content.platform_id }}</a-descriptions-item>
                  <a-descriptions-item label="内容类型">{{ content.content_type || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="处理链接" :span="2">
                    <a v-if="content.handle_link" :href="content.handle_link" target="_blank" class="link">{{ content.handle_link }}</a>
                    <span v-else>-</span>
                  </a-descriptions-item>
                </a-descriptions>
                <div v-if="content.exinfos" class="exinfos-box">
                  <div class="exinfos-title">扩展信息</div>
                  <div v-if="content.exinfos.project_info" class="exinfos-item">
                    <span class="exinfos-label">项目信息:</span>
                    <span v-if="content.exinfos.project_info.name">
                      {{ content.exinfos.project_info.name }}
                      <a-tag v-if="content.exinfos.project_info.level" color="orange">{{ content.exinfos.project_info.level }}</a-tag>
                    </span>
                    <span v-else>-</span>
                  </div>
                  <div v-if="content.exinfos.shipment_status !== undefined && content.exinfos.shipment_status !== null" class="exinfos-item">
                    <span class="exinfos-label">发货状态:</span>
                    <a-tag v-if="content.exinfos.shipment_status === 0">未发货</a-tag>
                    <a-tag v-else-if="content.exinfos.shipment_status === 1" color="green">已发货</a-tag>
                    <a-tag v-else>{{ content.exinfos.shipment_status }}</a-tag>
                  </div>
                  <div v-if="content.exinfos.sku && content.exinfos.sku.length" class="exinfos-item">
                    <span class="exinfos-label">SKU 列表:</span>
                    <div class="sku-list">
                      <a-tag v-for="sku in content.exinfos.sku" :key="sku" class="sku-tag">{{ sku }}</a-tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentRow.record_list && currentRow.record_list.length" class="detail-section">
              <div class="section-title">操作记录</div>
              <a-timeline>
                <a-timeline-item v-for="record in currentRow.record_list" :key="record.id" :color="record.record_type === 2 ? 'green' : 'blue'">
                  <div class="record-time">{{ record.create_time }}</div>
                  <div class="record-card">
                    <div class="record-type">
                      <a-tag v-if="record.record_type === 1" color="blue">评论</a-tag>
                      <a-tag v-else-if="record.record_type === 2" color="green">状态变更</a-tag>
                    </div>
                    <div class="record-comment">{{ record.comment }}</div>
                    <div v-if="record.old_status !== undefined && record.new_status !== undefined" class="record-status-change">
                      <span>状态: </span>
                      <a-tag v-if="record.old_status === 0" color="red">未处理</a-tag>
                      <a-tag v-else-if="record.old_status === 1">已取消</a-tag>
                      <a-tag v-else-if="record.old_status === 2" color="green">已完成</a-tag>
                      <a-tag v-else>{{ record.old_status }}</a-tag>
                      <span style="margin: 0 8px">→</span>
                      <a-tag v-if="record.new_status === 0" color="red">未处理</a-tag>
                      <a-tag v-else-if="record.new_status === 1">已取消</a-tag>
                      <a-tag v-else-if="record.new_status === 2" color="green">已完成</a-tag>
                      <a-tag v-else>{{ record.new_status }}</a-tag>
                    </div>
                    <div class="record-user">操作人: {{ record.update_user }}</div>
                  </div>
                </a-timeline-item>
              </a-timeline>
            </div>

            <div v-if="currentRow.attachment_list && currentRow.attachment_list.length" class="detail-section">
              <div class="section-title">附件</div>
              <a-descriptions :column="1" bordered>
                <a-descriptions-item v-for="(att, idx) in currentRow.attachment_list" :key="idx" :label="'附件 ' + (idx + 1)">
                  <a :href="att.url" target="_blank">{{ att.name || att.url }}</a>
                </a-descriptions-item>
              </a-descriptions>
            </div>

            <div class="dialog-actions">
              <a-button @click="detailDialogVisible = false">关闭</a-button>
            </div>
          </template>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import { getTodoList, getCategoryList, getTodoDetail } from '@/api/todo'

const STATUS_OPTIONS = [
  { value: 0, label: '未处理', color: 'blue' },
  { value: 1, label: '已取消', color: 'green' },
  { value: 2, label: '已完成', color: 'orange' },
  { value: 3, label: '待确认', color: 'red' }
]

export default {
  data() {
    return {
      searchForm: {
        participant: '',
        subject: '',
        category_id: undefined,
        status: undefined,
        is_overdue: undefined
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0
      },
      dataSource: [],
      loading: false,
      categoryLoading: false,
      categoryList: [],
      tableScrollY: 0,
      detailDialogVisible: false,
      detailLoading: false,
      currentRow: {},
      statusOptions: STATUS_OPTIONS,
      statusMap: STATUS_OPTIONS.reduce((acc, cur) => {
        acc[cur.value] = cur.label
        return acc
      }, {}),
      statusColorMap: STATUS_OPTIONS.reduce((acc, cur) => {
        acc[cur.value] = cur.color
        return acc
      }, {}),
      columns: [
        {
          title: 'id',
          dataIndex: 'id',
          width: 120
        },
        {
          title: '待办编号',
          dataIndex: 'code',
          width: 160
        },
        {
          title: '待办主题',
          dataIndex: 'subject',
          width: 160
        },
        {
          title: '待办分类',
          dataIndex: 'category_name',
          width: 160
        },
        {
          title: '待办任务状态',
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
          width: 80
        },
        {
          title: '开始时间 / 更新时间',
          scopedSlots: { customRender: 'create_time' },
          width: 160
        },
        {
          title: '取消时间 / 完成时间',
          scopedSlots: { customRender: 'cancel_time' },
          width: 160
        },
        {
          title: '超时天数',
          dataIndex: 'human_timeout',
          width: 120
        },
        {
          title: '处理人',
          dataIndex: 'participant',
          width: 100
        },
        {
          title: '操作',
          key: 'operation',
          scopedSlots: { customRender: 'operation' },
          width: 100
        }
      ]
    }
  },

  created() {
    this.loadCategoryList()
  },

  mounted() {
    this.loadData()
    this.calcTableHeight()
    window.addEventListener('resize', this.calcTableHeight)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight)
  },

  methods: {
    calcTableHeight() {
      const pageHeight = window.innerHeight
      const headerHeight = 64
      const contentMargin = 48
      const contentPadding = 48
      const paginationHeight = 52
      const container = document.querySelector('.todo-list-container .search-form')
      const searchHeight = container ? container.clientHeight : 52
      const tableHeaderHeight = 55
      const extraGap = 10
      this.tableScrollY = pageHeight - headerHeight - contentMargin - contentPadding - searchHeight - paginationHeight - tableHeaderHeight - extraGap
    },

    async loadCategoryList() {
      try {
        this.categoryLoading = true
        const params = {
          page_num: 1,
          page_size: 500
        }
        const { data } = await getCategoryList(params)
        this.categoryList = data.list
      } catch (error) {
        this.$message.error('数据加载失败')
      } finally {
        this.categoryLoading = false
      }
    },

    async loadData() {
      try {
        this.loading = true
        const params = {
          page_num: this.pagination.current,
          page_size: this.pagination.pageSize,
          ...this.searchForm
        }

        const { data } = await getTodoList(params)
        this.dataSource = data.list
        this.pagination.total = data.total
      } catch (error) {
        this.$message.error('数据加载失败')
      } finally {
        this.loading = false
      }
    },

    viewDetail(row) {
      this.currentRow = { id: row.id }
      this.detailDialogVisible = true
      this.detailLoading = true
      getTodoDetail(row.id)
        .then(res => {
          if (res.code === 0) {
            this.currentRow = res.data
          }
          this.detailLoading = false
        })
        .catch(() => {
          this.detailLoading = false
        })
    },

    onDetailClosed() {
      this.currentRow = {}
    },

    parseSku(skuStr) {
      if (!skuStr) return []
      try {
        const parsed = JSON.parse(skuStr)
        if (Array.isArray(parsed)) return parsed
        return [skuStr]
      } catch (e) {
        return [skuStr]
      }
    },

    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    handleReset() {
      this.searchForm = {
        participant: '',
        subject: ''
      }
      this.handleSearch()
    },

    handlePageChange(page) {
      this.pagination.current = page
      this.loadData()
    },

    handlePageSizeChange(current, size) {
      this.pagination.pageSize = size
      this.handleSearch()
    }
  }
}
</script>

<style scoped>
.todo-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-form {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  flex-shrink: 0;
}

.compact-time div {
  line-height: 1.3;
  margin: 0;
  padding: 0;
}

.detail-content {
  min-height: 200px;
}

.detail-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 4px solid #409eff;
  color: #303133;
}

.content-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fafafa;
}

.content-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.content-index {
  font-weight: bold;
  color: #303133;
}

.sku-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sku-tag {
  margin: 0;
}

.exinfos-box {
  margin-top: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.exinfos-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #606266;
}

.exinfos-item {
  margin-bottom: 6px;
  display: flex;
  align-items: flex-start;
}

.exinfos-label {
  color: #909399;
  margin-right: 8px;
  min-width: 70px;
  flex-shrink: 0;
}

.record-card {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
}

.record-type {
  margin-bottom: 6px;
}

.record-time {
  color: #909399;
  font-size: 12px;
  margin-bottom: 6px;
}

.record-comment {
  color: #303133;
  margin-bottom: 6px;
  font-size: 14px;
}

.record-status-change {
  margin-bottom: 6px;
  font-size: 13px;
}

.record-user {
  color: #909399;
  font-size: 12px;
}

.link {
  color: #409eff;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.dialog-actions {
  margin-top: 24px;
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}
</style>
